/**
 * Dev-only tracing for the offer modal, covering both of the symptoms we are
 * chasing: how long each load phase takes (and whether it was served from a
 * cache), and whether each modal instance is actually garbage collected after
 * it closes.
 *
 * Usage from the browser console:
 *   __offerTrace.enable()   // then reload
 *   ...open and close the modal a few times...
 *   __offerTrace.report()   // paste the output
 *
 * Everything is inert unless explicitly enabled, so this is safe to ship.
 */

const STORAGE_KEY = 'litta:offerTrace'

interface TraceEvent {
    /** ms since the cycle started. */
    at: number
    name: string
    detail?: string
}

interface CycleRecord {
    cycle: number
    jobId: string
    startedAtMs: number
    events: TraceEvent[]
    carrierCount: number | null
    carriersReadyAt: number | null
    closedAt: number | null
    heapAtOpenMb: number | null
    heapAtCloseMb: number | null
    /** Set by the FinalizationRegistry once the instance is collected. */
    collected: boolean
}

let enabled = false
try {
    enabled = localStorage.getItem(STORAGE_KEY) === '1'
} catch {
    enabled = false
}

// console.info is hidden when the console's level filter is narrowed, which
// makes the tracer look broken. Everything here uses console.log.
const log = (...args: unknown[]) => console.log('[offerTrace]', ...args)

const cycles: CycleRecord[] = []
let current: CycleRecord | null = null

const heapMb = (): number | null => {
    const memory = (
        performance as Performance & { memory?: { usedJSHeapSize: number } }
    ).memory
    if (!memory) return null
    return Math.round((memory.usedJSHeapSize / 1024 / 1024) * 10) / 10
}

const registry =
    typeof FinalizationRegistry === 'undefined'
        ? null
        : new FinalizationRegistry<number>(cycle => {
              const record = cycles.find(entry => entry.cycle === cycle)
              if (record) record.collected = true
          })

export const isOfferTraceEnabled = () => enabled

export function traceCycleStart(jobId: string): void {
    if (!enabled) return
    current = {
        cycle: cycles.length + 1,
        jobId,
        startedAtMs: performance.now(),
        events: [],
        carrierCount: null,
        carriersReadyAt: null,
        closedAt: null,
        heapAtOpenMb: heapMb(),
        heapAtCloseMb: null,
        collected: false,
    }
    cycles.push(current)
    log(`cycle ${current.cycle} open — job ${jobId}`)
}

/**
 * `token` is the object whose lifetime we care about. If it is still reachable
 * after the modal closes, the modal leaked.
 */
export function traceRetention(token: object): void {
    if (!enabled || !current || !registry) return
    registry.register(token, current.cycle)
}

export function traceEvent(name: string, detail?: string): void {
    if (!enabled || !current) return
    const at = Math.round(performance.now() - current.startedAtMs)
    current.events.push({ at, name, detail })
    log(`+${at}ms ${name}${detail ? ` — ${detail}` : ''}`)
}

/** Wraps an async load so we record its duration under one name. */
export async function traceAsync<T>(
    name: string,
    run: () => Promise<T>,
): Promise<T> {
    if (!enabled) return run()
    const startedAt = performance.now()
    try {
        return await run()
    } finally {
        traceEvent(name, `${Math.round(performance.now() - startedAt)}ms`)
    }
}

export function traceCarriersReady(count: number): void {
    if (!enabled || !current) return
    if (current.carriersReadyAt !== null) return
    current.carrierCount = count
    current.carriersReadyAt = Math.round(
        performance.now() - current.startedAtMs,
    )
    log(`+${current.carriersReadyAt}ms carriers rendered — ${count} rows`)
}

export function traceCycleEnd(): void {
    if (!enabled || !current) return
    current.closedAt = Math.round(performance.now() - current.startedAtMs)
    current.heapAtCloseMb = heapMb()
    log(`cycle ${current.cycle} closed`)
    current = null
}

interface TraceReport {
    summary: Record<string, unknown>[]
    phases: Record<string, unknown>[]
    retention: string
    note: string
}

function buildReport(): TraceReport {
    const summary = cycles.map(cycle => ({
        cycle: cycle.cycle,
        rows: cycle.carrierCount,
        'carriers ready (ms)': cycle.carriersReadyAt,
        'heap at open (MB)': cycle.heapAtOpenMb,
        'heap at close (MB)': cycle.heapAtCloseMb,
        collected: cycle.collected ? 'yes' : 'NO — retained',
    }))

    // Phase durations side by side so a cache hit on cycle 2+ is obvious.
    const names = [...new Set(cycles.flatMap(c => c.events.map(e => e.name)))]
    const phases = names.map(name => {
        const row: Record<string, unknown> = { phase: name }
        for (const cycle of cycles) {
            const event = cycle.events.find(e => e.name === name)
            // Fall back to the timestamp: events with no detail (such as the
            // job doc arriving) previously rendered as a bare dash.
            row[`cycle ${cycle.cycle}`] = !event
                ? '—'
                : (event.detail ?? `at +${event.at}ms`)
        }
        return row
    })

    const retained = cycles.filter(c => c.closedAt !== null && !c.collected)

    return {
        summary,
        phases,
        retention: `${retained.length} of ${cycles.filter(c => c.closedAt !== null).length} closed modals still retained`,
        note: 'Heap figures are only meaningful after a manual GC (DevTools > Memory > bin icon), and retention is only accurate once GC has run.',
    }
}

function report(): TraceReport | string {
    if (cycles.length === 0) {
        const why = enabled
            ? 'Tracing is on but no modal has been opened yet — open and close the offer modal, then run this again.'
            : 'Tracing is off. Run __offerTrace.enable(), then open and close the offer modal.'
        log(why)
        return why
    }
    const built = buildReport()
    log('=== offer modal trace ===')
    console.table(built.summary)
    console.table(built.phases)
    log(built.retention)
    log(built.note)
    log('Copy/paste friendly:\n' + JSON.stringify(built, null, 2))
    return built
}

declare global {
    interface Window {
        __offerTrace?: {
            enable: () => string
            disable: () => string
            status: () => string
            report: () => TraceReport | string
            gc: () => Promise<string>
            reset: () => string
            raw: () => CycleRecord[]
        }
    }
}

if (typeof window !== 'undefined') {
    window.__offerTrace = {
        // Takes effect immediately: the previous version only read the flag at
        // module load, so enable() followed by report() recorded nothing.
        enable: () => {
            enabled = true
            try {
                localStorage.setItem(STORAGE_KEY, '1')
            } catch {
                // Private mode — tracing still works for this page load.
            }
            const message =
                'Tracing ON. Open and close the offer modal, then run __offerTrace.report().'
            log(message)
            return message
        },
        disable: () => {
            enabled = false
            try {
                localStorage.removeItem(STORAGE_KEY)
            } catch {
                // Nothing to clear.
            }
            const message = 'Tracing OFF.'
            log(message)
            return message
        },
        status: () => {
            const message = `Tracing ${enabled ? 'ON' : 'OFF'}, ${cycles.length} cycle(s) recorded.`
            log(message)
            return message
        },
        report,
        // Retention is only trustworthy once a GC has actually run, otherwise
        // every cycle reports as retained whether or not it leaked.
        gc: async () => {
            const forceGc = (window as Window & { gc?: () => void }).gc
            if (!forceGc) {
                const message =
                    'No window.gc available. Use DevTools > Memory > the bin icon to force a GC, then run __offerTrace.report().'
                log(message)
                return message
            }
            forceGc()
            // FinalizationRegistry callbacks are queued, not synchronous.
            await new Promise(resolve => setTimeout(resolve, 100))
            const collected = cycles.filter(c => c.collected).length
            const message = `GC forced. ${collected} of ${cycles.length} cycle(s) collected.`
            log(message)
            return message
        },
        reset: () => {
            cycles.length = 0
            current = null
            const message = 'Cleared recorded cycles.'
            log(message)
            return message
        },
        raw: () => cycles,
    }

    if (import.meta.env.DEV || enabled) {
        log(
            enabled
                ? 'ready, tracing is ON — open and close the offer modal, then __offerTrace.report()'
                : 'ready — run __offerTrace.enable() to start tracing',
        )
    }
}
